import React, { useMemo, useState } from 'react';
import { message, Modal, Spin } from 'antd';
import useSupportPracticeList from 'hooks/useSupportPracticeList';
import useCurrentUser from 'hooks/useCurrentUser';
import { switchProviderIdentity } from 'api/group';
import { setScheduleToken } from 'utils/localstore';
import { ERoleCode } from 'types/enumItems';
import { TGroupProvider, TPractice, TSwitchProviderResponse } from 'types/group';
import ProviderStore from 'store/Provider';
import s from './s.module.less';
import { PATH } from 'constants/path';
import { handleSwitchSuccess } from 'utils/auth';

const ROLE_DISPLAY: Record<string, string> = {
    [ERoleCode.GROUP_OWNER]: 'Group Owner',
    [ERoleCode.GROUP_PROVIDER]: 'Provider',
    [ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT]: 'Administrative Assistant',
    [ERoleCode.GROUP_CLINICAL_ASSISTANT]: 'Clinical Assistant',
    [ERoleCode.GROUP_PRACTICE_MANAGER]: 'Practice Manager',
};

interface PracticeSwitcherProps {
    open: boolean;
    onClose: () => void;
}

const PracticeSwitcher: React.FC<PracticeSwitcherProps> = ({ open, onClose }) => {
    const [switching, setSwitching] = useState(false);
    const [expandedOrgId, setExpandedOrgId] = useState<number | null>(null);
    const [practiceList] = useSupportPracticeList();
    const [currentUser] = useCurrentUser();
    const [, set] = ProviderStore.useStore();

    const currentOrgId = currentUser?.organizationId ?? 0;
    const currentProviderId = currentUser?.provider?.id ?? 0;

    const defaultExpandedOrgId = useMemo(() => currentOrgId, [currentOrgId]);

    const handleToggleExpand = (orgId: number) => {
        setExpandedOrgId((prev) => (prev === orgId ? null : orgId));
    };

    const isExpanded = (orgId: number) => {
        if (expandedOrgId !== null) return expandedOrgId === orgId;
        return orgId === defaultExpandedOrgId;
    };

    const handleSelectProvider = async (practice: TPractice, provider: TGroupProvider) => {
        //owner cannot switch to other provider
        // if (practice.roleCode === ERoleCode.GROUP_OWNER) {
        //     message.error('Owner cannot switch to other provider');
        //     return;
        // }
        if (practice.orgId === currentOrgId && provider.providerId === currentProviderId) {
            onClose();
            return;
        }

        setSwitching(true);
        try {
            const res = await switchProviderIdentity(provider.providerId);
            if (res?.error) {
                message.error(res.error);
            } else {
                const data: TSwitchProviderResponse = res?.data?.data;

                const token = data?.token;
                const authPaylod = data?.authPaylod;
                if (authPaylod && token) {
                    handleSwitchSuccess(set, authPaylod, token);
                }
                const roleCode = data?.roleCode;
                const _isAdmin =
                    data?.isAdmin ||
                    roleCode === ERoleCode.GROUP_PRACTICE_MANAGER ||
                    roleCode === ERoleCode.GROUP_OWNER;
                const path = _isAdmin ? undefined : `${PATH.DASHBOARD}/${PATH.CLIENTS}`;
                if (token) {
                    setScheduleToken(token);
                    message.success('Practice switched successfully');
                    setTimeout(() => {
                        if (path) {
                            window.location.href = path;
                        } else {
                            window.location.reload();
                        }
                    }, 500);
                }
            }
        } catch (e) {
            message.error('Failed to switch practice');
        } finally {
            setSwitching(false);
            onClose();
        }
    };

    return (
        <Modal open={open} onCancel={onClose} footer={null} closable={false} centered width={400} className={s.modal}>
            <Spin spinning={switching}>
                <div className={s.panel}>
                    <div className={s.panelHeader}>Switch Account</div>
                    {practiceList?.map((practice) => {
                        let practiceName = practice.organizationName;
                        if (!practiceName && practice.providers.length > 0) {
                            practiceName = `${practice.providers[0]?.name}'s practice`;
                        }
                        const expanded = isExpanded(practice.orgId);

                        return (
                            <div key={practice.orgId}>
                                <div
                                    className={`${s.practiceItem} ${currentOrgId === practice.orgId ? s.practiceItemActive : ''}`}
                                    onClick={() => handleToggleExpand(practice.orgId)}
                                >
                                    <div className={s.practiceInfo}>
                                        <span className={s.practiceName}>{practiceName}</span>
                                        <div className={s.practiceRoles}>
                                            <span className={`${s.roleBadge} ${s.roleBadgeDefault}`}>
                                                {ROLE_DISPLAY[practice.roleCode] || practice.roleCode}
                                            </span>
                                            {practice.isAdmin && (
                                                <span className={`${s.roleBadge} ${s.roleBadgeAdmin}`}>Admin</span>
                                            )}
                                        </div>
                                    </div>
                                    <svg
                                        className={`${s.chevron} ${expanded ? s.chevronExpanded : ''}`}
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="6 4 10 8 6 12" />
                                    </svg>
                                </div>
                                {expanded && practice.providers?.length > 0 && (
                                    <div className={s.providerList}>
                                        {practice.providers.map((provider) => {
                                            const isActive =
                                                practice.orgId === currentOrgId &&
                                                provider.providerId === currentProviderId;

                                            return (
                                                <div
                                                    key={provider.providerId}
                                                    className={`${s.providerItem} ${isActive ? s.providerItemActive : ''}`}
                                                    onClick={() => handleSelectProvider(practice, provider)}
                                                >
                                                    <span className={s.providerName}>
                                                        {provider.name}
                                                        {provider.isAdmin && (
                                                            <span className={`${s.roleBadge} ${s.roleBadgeAdmin}`}>Admin</span>
                                                        )}
                                                    </span>
                                                    {isActive && (
                                                        <svg
                                                            className={s.providerCheck}
                                                            viewBox="0 0 16 16"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2.2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <polyline points="3 8.5 6.5 12 13 4.5" />
                                                        </svg>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Spin>
        </Modal>
    );
};

export default PracticeSwitcher;
